import { z } from 'zod';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnboardingFormFields } from '@Types/types';
import { useFormHook } from '@Hooks/index';
import { useBoundStore } from '@Store/index';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { AuthErrorMap } from '@Services/errors/auth.errors';
import { UboOnboardingAnalyticsService } from '../UboAnalytcs.service';
import { useSubmitSSNMutation } from './useSubmitSSNMutation';
import type { ServerErrorResponse } from '@Types/types';
export const useIdVerificationLogic = () => {
  const { isUs } = useBoundStore((state) => state.resetPasswordSlice);
  const { t } = useTranslation();
  const customerId = useBoundStore((state) => state.secondaryCustomerId);
  const phoneStateToken = useBoundStore((state) => state.phoneStateToken);
  const { navigateToNextPage } = useNavigationManager();
  const idVerificationFormScheme = z.object({
    [OnboardingFormFields.MFA_OTP]: z.string(),
  });
  const setUboToken = useBoundStore((state) => state.setUboToken);
  const [customError, setCustomError] = useState<string>('');

  type IdVerificationForm = z.infer<typeof idVerificationFormScheme>;
  const defaultValues: IdVerificationForm = {
    [OnboardingFormFields.MFA_OTP]: '',
  };

  const formMethods = useFormHook({
    formSchema: idVerificationFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'validate-id-verification-form',
    reValidateMode: 'onBlur',
  });

  const { mutateAsync, isPending } = useSubmitSSNMutation();

  const handleValidateCode = async (code: string) => {
    if (!customerId || !phoneStateToken) return;
    try {
      const response = await mutateAsync({
        customerId,
        phoneStateToken,
        answer: code,
      });
      setUboToken(response?.token);
      UboOnboardingAnalyticsService.ssnValidate();
      navigateToNextPage(RoutesMap.UBO_ONBOARDING_ID_VERIFICATION);
    } catch (error: unknown) {
      const serverError = error as ServerErrorResponse;
      if (
        serverError?.response?.data?.errors[0]?.code === AuthErrorMap.MFA_CODE_IS_INVALID.errorCode
      ) {
        setCustomError(
          isUs ? t('recovery.securityQuestion.ssnError') : t('recovery.securityQuestion.idError')
        );
      } else {
        setCustomError(serverError?.response?.data?.errors[0]?.localizedMessage || '');
        throw error;
      }
    }
  };

  return {
    formMethods,
    handleValidateCode,
    isLoading: isPending,
    customError,
    setCustomError,
  };
};
